import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { NotificationsService, PaginatedNotifications } from './notificationsService';
import { Notification } from './types';
import { ThreeDotMenuButton } from '@/styles/StandardButtons';
import { NavigationService } from '@/services/navigationService';
import { fetchTeamMemberLookup } from '@/lib/entity-api';
import { colors } from '@/design-system/colors';

export function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [selectedNotifications, setSelectedNotifications] = useState<string[]>([]);
  const [userNames, setUserNames] = useState<Map<string, string>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // Fetch user names for comment notifications
  const fetchUserNames = useCallback(async (notifications: Notification[]) => {
    const userIds = new Set<string>();
    const emailMap = new Map<string, string>();

    notifications.forEach(notification => {
      const metadata = notification.metadata || {};
      const commenterId = metadata.commenter_id;
      const fromEmail = metadata.from_email;
      
      if (commenterId) {
        userIds.add(commenterId);
      }
      if (fromEmail) {
        // Store email for fallback
        emailMap.set(commenterId || '', fromEmail);
      }
    });

    if (userIds.size === 0) {
      return;
    }

    try {
      // The company's people (generic lookup, by the API); a commenter who is no longer an active member falls back to their email below
      const wanted = new Set(userIds);
      const users = (await fetchTeamMemberLookup()).filter((person) => wanted.has(person.id));

      const nameMap = new Map<string, string>();
      (users || []).forEach((user: any) => {
        const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
        nameMap.set(user.id, fullName || user.email || 'Unknown User');
      });

      // Add email fallbacks for users not found
      emailMap.forEach((email, id) => {
        if (!nameMap.has(id) && email) {
          nameMap.set(id, email);
        }
      });

      setUserNames(nameMap);
    } catch (error) {
      console.error('Error fetching user names:', error);
    }
  }, []);

  // Get display name for commenter
  const getCommenterName = (notification: Notification): string => {
    const metadata = notification.metadata || {};
    const commenterId = metadata.commenter_id;
    
    if (commenterId && userNames.has(commenterId)) {
      return userNames.get(commenterId)!;
    }
    
    // Fallback to email if available
    if (metadata.from_email) {
      return metadata.from_email;
    }
    
    return 'Someone';
  };

  // Format notification description with user name
  const formatNotificationDescription = (notification: Notification): string => {
    const description = notification.description;
    const eventName = notification.event_name?.toLowerCase() || '';
    
    // Check if it's a comment notification
    if (eventName.includes('comment') || description.toLowerCase().includes('commented')) {
      const commenterName = getCommenterName(notification);
      // Replace "Someone" with actual name
      return description.replace(/Someone/gi, commenterName);
    }
    
    return description;
  };

  // Extract entity type and ID from notification metadata
  const getEntityInfo = (notification: Notification): { entityType: string | null; entityId: string | null } => {
    const metadata = notification.metadata || {};
    const entityType = metadata.entity_type;
    // Try different possible ID fields based on entity type
    const entityId = metadata.entity_id 
      || metadata.task_id 
      || metadata.checklist_id 
      || metadata.specification_id 
      || metadata.warranty_id 
      || metadata.change_order_id
      || metadata.material_order_id
      || metadata.purchase_order_id
      || metadata.bill_id
      || metadata.invoice_id
      || metadata.bid_id
      || metadata.proposal_id;
    
    // If no entity_type but we have an ID, try to infer from event_name
    if (!entityType && entityId) {
      const eventName = notification.event_name?.toLowerCase() || '';
      if (eventName.includes('task') || eventName.includes('todo')) {
        return { entityType: 'task', entityId };
      }
      if (eventName.includes('checklist')) {
        return { entityType: 'checklist', entityId };
      }
      if (eventName.includes('specification')) {
        return { entityType: 'specification', entityId };
      }
      if (eventName.includes('warranty')) {
        return { entityType: 'warranty', entityId };
      }
    }
    
    return { entityType, entityId };
  };

  const loadNotifications = useCallback(async (reset: boolean = false) => {
    const currentOffset = reset ? 0 : offset;
    
    if (reset) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const result: PaginatedNotifications = await NotificationsService.getNotifications(
        undefined, // Use default page size
        currentOffset
      );

      if (reset) {
        setNotifications(result.notifications);
        setOffset(result.notifications.length);
        // Fetch user names after loading notifications
        fetchUserNames(result.notifications);
      } else {
        setNotifications(prev => {
          const updated = [...prev, ...result.notifications];
          fetchUserNames(updated);
          return updated;
        });
        setOffset(prev => prev + result.notifications.length);
      }

      setHasMore(result.hasMore);
      setTotalCount(result.totalCount);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [offset, fetchUserNames]);

  // Initial load
  useEffect(() => {
    loadNotifications(true);
  }, []);

  // Infinite scroll setup
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          loadNotifications(false);
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, isLoadingMore, loadNotifications]);

  const handleNotificationClick = async (notification: Notification, event?: React.MouseEvent) => {
    // Don't navigate if clicking on checkbox
    if (event && (event.target as HTMLElement).closest('button, [role="checkbox"]')) {
      return;
    }

    // Mark as read
    const success = await NotificationsService.markAsRead(notification.id);
    if (success) {
      setNotifications(prev => 
        prev.map(n => 
          n.id === notification.id ? { ...n, is_read: true } : n
        )
      );
    }
    
    // Get entity info and navigate using NavigationService
    const { entityType, entityId } = getEntityInfo(notification);
    if (entityType && entityId) {
      NavigationService.openEntity(entityType, entityId, navigate);
    } else {
      // Fallback: just navigate to notifications page if we can't determine entity
      navigate('/notifications');
    }
  };

  const handleSelectNotification = (notificationId: string, checked: boolean) => {
    setSelectedNotifications(prev => {
      if (checked) {
        return [...prev, notificationId];
      } else {
        return prev.filter(id => id !== notificationId);
      }
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedNotifications(notifications.map(n => n.id));
    } else {
      setSelectedNotifications([]);
    }
  };

  const handleMarkSelectedAsRead = async () => {
    if (selectedNotifications.length === 0) return;

    const promises = selectedNotifications.map(id => NotificationsService.markAsRead(id));
    await Promise.all(promises);

    setNotifications(prev => 
      prev.map(n => 
        selectedNotifications.includes(n.id) ? { ...n, is_read: true } : n
      )
    );
    setSelectedNotifications([]);
  };

  const handleMarkSelectedAsUnread = async () => {
    if (selectedNotifications.length === 0) return;

    const promises = selectedNotifications.map(id => NotificationsService.markAsUnread(id));
    await Promise.all(promises);

    setNotifications(prev => 
      prev.map(n => 
        selectedNotifications.includes(n.id) ? { ...n, is_read: false } : n
      )
    );
    setSelectedNotifications([]);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const notificationDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (notificationDate.getTime() === today.getTime()) {
      // Today - show time
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } else if (notificationDate.getTime() === yesterday.getTime()) {
      // Yesterday
      return 'Yesterday';
    } else {
      // Older - show date
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const truncateText = (text: string, maxLength: number = 60) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;
  const allSelected = notifications.length > 0 && selectedNotifications.length === notifications.length;
  const someSelected = selectedNotifications.length > 0 && selectedNotifications.length < notifications.length;

  const menuItems = [
    {
      label: 'Mark selected as read',
      icon: null,
      onClick: handleMarkSelectedAsRead,
      disabled: selectedNotifications.length === 0,
    },
    {
      label: 'Mark selected as unread',
      icon: null,
      onClick: handleMarkSelectedAsUnread,
      disabled: selectedNotifications.length === 0,
    },
  ];

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
        <ThreeDotMenuButton items={menuItems} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {totalCount > 0 ? (
              <>
                {unreadCount > 0 ? `${unreadCount} unread` : 'All notifications read'} 
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({totalCount} total)
                </span>
              </>
            ) : (
              'No notifications yet'
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {notifications.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No notifications yet
            </div>
          ) : (
            <>
              <div className="bg-white border border-gray-200 rounded-lg">
                {/* Header with select all checkbox */}
                <div className="flex items-center px-4 py-2 border-b border-gray-200 bg-gray-50">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={handleSelectAll}
                    className="h-4 w-4"
                  />
                  <span className="ml-2 text-sm text-gray-600">
                    {selectedNotifications.length > 0 
                      ? `${selectedNotifications.length} selected`
                      : 'Select all'}
                  </span>
                </div>

                {/* Notifications list */}
                {notifications.map((notification) => {
                  const isSelected = selectedNotifications.includes(notification.id);
                  const formattedDescription = formatNotificationDescription(notification);
                  
                  return (
                    <div
                      key={notification.id}
                      className="flex items-center py-3 px-4 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      onClick={(e) => handleNotificationClick(notification, e)}
                    >
                      {/* Checkbox */}
                      <div className="mr-3 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => handleSelectNotification(notification.id, checked as boolean)}
                          className="h-4 w-4"
                        />
                      </div>

                      {/* Unread indicator */}
                      {!notification.is_read && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-3 flex-shrink-0" />
                      )}
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0 pr-3">
                        <p className={`text-sm truncate ${!notification.is_read ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                          {truncateText(formattedDescription)}
                        </p>
                      </div>
                      
                      {/* Time/Date */}
                      <div className="flex-shrink-0">
                        <p className="text-xs text-gray-500">
                          {formatDateTime(notification.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Infinite scroll trigger */}
              {hasMore && (
                <div ref={loadMoreRef} className="flex justify-center py-4">
                  {isLoadingMore ? (
                    <div className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-32"></div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">
                      Scroll to load more...
                    </div>
                  )}
                </div>
              )}

              {!hasMore && notifications.length > 0 && (
                <div className="text-center py-4 text-sm text-gray-500">
                  You've reached the end of your notifications
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
